// crewbit-admin/src/components/money/settings/TaxModeWizard.tsx
//
// 3-step modal-overlay wizard for tax_mode -> 'quick_method' transition only,
// per Decision §Settings.D4 + §3c LOCKED + Tier 2-UI Phase C upgrade:
//   Step 1: Eligibility + auto-derived rate (Phase C — fuses CRA RC4058 derived
//           rate display with trailing-4q eligibility check from
//           derive_quick_method_config substrate; per §0.13 surfaces the rate
//           with reasoning + collapsible "Edit manually" override affordance).
//   Step 2: Confirm understanding (checkbox: ITC claims excluded under QM).
//   Step 3: Effective-date display + Confirm.
//
// Eligibility is now SERVER-ENFORCED (substrate trailing-4q revenue gate;
// §Tax.QuickMethodEligibilityServerSide CLOSED via Phase C derive RPC).
// Quebec carve-out renders gracefully when substrate returns
// QUEBEC_QM_NOT_SUPPORTED_V1 — wizard renders explanatory copy + back-out.
//
// Override flow: operator types percent (e.g., "8.8") in collapsed manual
// input; saved as decimal (0.088) via quick_method_remittance_rate_override
// column. Three-tier resolution at filing time: override → derived → fallback.
//
// Modal idiom: position:fixed overlay matching MarkPaidModal.

import { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import { formatMoney } from '../../../lib/money';
import type { DeriveQuickMethodConfigSuccess } from '../../../types/quickMethod';

type QuickMethodConfigData = Omit<DeriveQuickMethodConfigSuccess, 'success'>;

interface TaxModeWizardProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Caller wraps useUpdateBusinessMoneySettings with both taxMode + override args.
   * `rateOverride` is decimal 0..1 (e.g., 0.088 for 8.8%), null = use substrate-derived.
   */
  onConfirm: (args: {
    taxMode: 'quick_method';
    rateOverride: number | null;
  }) => Promise<{ success: boolean; message?: string }>;
  /** Substrate-derived QM config from useDeriveQuickMethodConfig (null while loading). */
  quickMethodConfig: QuickMethodConfigData | null;
  configLoading: boolean;
  /** Substrate-mapped error copy (e.g., QUEBEC_QM_NOT_SUPPORTED_V1). null when no error. */
  configError: string | null;
  /** True when configError stems from Quebec carve-out — drives step 1 rendering. */
  isQuebecBlocked: boolean;
}

type Step = 1 | 2 | 3;

export default function TaxModeWizard({
  isOpen,
  onClose,
  onConfirm,
  quickMethodConfig,
  configLoading,
  configError,
  isQuebecBlocked,
}: TaxModeWizardProps) {
  const [step, setStep] = useState<Step>(1);
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [overrideExpanded, setOverrideExpanded] = useState(false);
  const [overrideInput, setOverrideInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset of wizard state on open transition
      setStep(1);
      setUnderstood(false);
      setBusy(false);
      setErrorMsg(null);
      setOverrideExpanded(false);
      setOverrideInput('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, busy, onClose]);

  if (!isOpen) return null;

  // Override validation: percent input (e.g., "8.8" → 0.088 decimal). Range 0..1 exclusive.
  const overrideValid = (() => {
    if (!overrideExpanded) return true;
    const trimmed = overrideInput.trim();
    if (trimmed === '') return false;
    const n = parseFloat(trimmed);
    if (!Number.isFinite(n)) return false;
    const decimal = n / 100;
    return decimal > 0 && decimal < 1;
  })();
  const overrideError =
    overrideExpanded && overrideInput.trim() !== '' && !overrideValid
      ? 'Rate must be between 0.1% and 99.9%.'
      : null;

  const handleConfirm = async () => {
    setBusy(true);
    setErrorMsg(null);
    const rateOverride =
      overrideExpanded && overrideInput.trim() !== ''
        ? parseFloat(overrideInput) / 100
        : null;
    const result = await onConfirm({ taxMode: 'quick_method', rateOverride });
    setBusy(false);
    if (result.success) {
      onClose();
    } else {
      setErrorMsg(result.message ?? 'Failed');
    }
  };

  const goNext = () => {
    if (step === 3) return;
    setStep((s) => (s + 1) as Step);
  };
  const goBack = () => {
    if (step === 1) return;
    setStep((s) => (s - 1) as Step);
  };

  // Step 1 advance gates: substrate must report eligible AND non-Quebec AND override (if entered) valid.
  const step1Advanceable =
    !configLoading &&
    !isQuebecBlocked &&
    quickMethodConfig?.eligible === true &&
    overrideValid;

  const canAdvance =
    (step === 1 && step1Advanceable) ||
    (step === 2 && understood) ||
    (step === 3 && !busy);

  return (
    <>
      <div
        onClick={busy ? undefined : onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 70,
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          cursor: busy ? 'not-allowed' : 'pointer',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Switch to Quick Method"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: 520,
          zIndex: 71,
          background: palette.white,
          borderRadius: 16,
          boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: spacing.lg, borderBottom: `1px solid ${palette.g150}`,
        }}>
          <div style={{ fontSize: typography.size.heading, fontWeight: 700, color: palette.g800 }}>
            Switch to Quick Method
          </div>
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            disabled={busy}
            aria-label="Cancel"
            style={{
              width: 32, height: 32, borderRadius: 16,
              border: 'none', background: 'transparent',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              cursor: busy ? 'not-allowed' : 'pointer',
              color: palette.g600,
            }}
          >
            <X size={20} strokeWidth={2.25} />
          </button>
        </div>

        {/* Step dots */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: spacing.sm,
          paddingBlock: spacing.md, background: palette.bg,
        }}>
          {[1, 2, 3].map((n) => {
            const isActive = n === step;
            const isDone = n < step;
            return (
              <div
                key={n}
                style={{
                  width: isActive ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: isActive
                    ? palette.accent
                    : isDone
                      ? palette.accentSoft
                      : palette.g200,
                  transition: 'all 200ms ease',
                }}
              />
            );
          })}
        </div>

        {/* Body */}
        <div style={{
          padding: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.md,
          overflowY: 'auto', flex: 1,
        }}>
          {step === 1 && (
            <>
              <div style={{ fontSize: typography.size.title, fontWeight: 700, color: palette.g800 }}>
                Eligibility & rate
              </div>

              {configLoading && (
                <div style={{ color: palette.g500, fontSize: typography.size.body }}>Loading…</div>
              )}

              {isQuebecBlocked && (
                <div style={{
                  background: palette.g100, padding: spacing.md, borderRadius: radii.md,
                  display: 'flex', flexDirection: 'column', gap: spacing.sm,
                }}>
                  <div style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
                    Quebec Quick Method
                  </div>
                  <div style={{ fontSize: typography.size.body, color: palette.g600, lineHeight: 1.5 }}>
                    Quebec administers Quick Method through Revenu Québec (FP-2074), not CRA RC4058.
                    CrewBit doesn&apos;t support Quebec QM yet — coming in a future release.
                  </div>
                </div>
              )}

              {!isQuebecBlocked && configError && (
                <div style={{ fontSize: typography.size.caption, color: palette.amber, fontWeight: 600 }}>
                  {configError}
                </div>
              )}

              {!isQuebecBlocked && quickMethodConfig && (
                <>
                  <MetricRow label="Trailing 4 quarters revenue" value={formatMoney(quickMethodConfig.trailing_4q_revenue)} />
                  <MetricRow label="Quick Method ceiling" value={formatMoney(quickMethodConfig.ceiling)} />
                  <div style={{
                    fontSize: typography.size.body,
                    color: quickMethodConfig.eligible ? palette.g800 : palette.amber,
                    fontWeight: 600, paddingBlock: spacing.xs,
                  }}>
                    {quickMethodConfig.eligible
                      ? 'Eligible for Quick Method election'
                      : 'Over ceiling — Quick Method not available; consider Standard charge mode.'}
                  </div>

                  {quickMethodConfig.eligible && (
                    <>
                      <div style={{ height: 1, background: palette.g150, marginBlock: spacing.sm }} />
                      <div style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g700 }}>
                        Your Quick Method rate
                      </div>
                      <div style={{
                        fontSize: 36, fontWeight: 700, color: palette.g800,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {(quickMethodConfig.rate * 100).toFixed(1)}%
                      </div>
                      <div style={{ fontSize: typography.size.caption, color: palette.g600 }}>
                        {quickMethodConfig.reasoning.rate_explanation}
                      </div>
                      <div style={{ fontSize: typography.size.caption, color: palette.g500, fontStyle: 'italic' }}>
                        {quickMethodConfig.reasoning.credit_explanation}
                      </div>

                      <button
                        type="button"
                        onClick={() => setOverrideExpanded((v) => !v)}
                        style={{
                          paddingBlock: spacing.sm, marginTop: spacing.xs,
                          border: 'none', background: 'transparent', textAlign: 'left',
                          fontSize: typography.size.body, color: palette.accent,
                          fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        {overrideExpanded ? 'Use derived rate' : 'Edit rate manually'}
                      </button>

                      {overrideExpanded && (
                        <div style={{
                          background: palette.g100, padding: spacing.md, borderRadius: radii.md,
                          display: 'flex', flexDirection: 'column', gap: spacing.xs,
                        }}>
                          <label style={{
                            fontSize: typography.size.caption, color: palette.g700, fontWeight: 600,
                          }}>Manual remittance rate (%)</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={overrideInput}
                            onChange={(e) => setOverrideInput(e.target.value)}
                            placeholder="e.g., 8.8"
                            aria-label="Manual remittance rate"
                            style={{
                              fontSize: typography.size.body, color: palette.g800,
                              background: palette.white, border: `1px solid ${palette.g200}`,
                              borderRadius: radii.sm, padding: '8px 12px', fontFamily: 'inherit',
                            }}
                          />
                          <div style={{ fontSize: typography.size.caption, color: palette.g500 }}>
                            Enter a value between 0.1 and 99.9 if your CRA documentation supports a non-default rate.
                          </div>
                          {overrideError && (
                            <div style={{ fontSize: typography.size.caption, color: palette.amber, fontWeight: 600 }}>
                              {overrideError}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ fontSize: typography.size.title, fontWeight: 700, color: palette.g800 }}>
                How Quick Method works
              </div>
              <div style={{ fontSize: typography.size.body, color: palette.g600, lineHeight: 1.5 }}>
                Under Quick Method you remit a flat percentage of your gross revenue. You don&apos;t claim
                Input Tax Credits (ITCs) on most expenses — that&apos;s the trade-off for the simpler math.
              </div>
              <button
                type="button"
                role="checkbox"
                aria-checked={understood}
                onClick={() => setUnderstood((v) => !v)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: spacing.sm,
                  padding: spacing.sm, border: 'none', background: 'transparent',
                  cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    width: 22, height: 22, borderRadius: 6,
                    border: `2px solid ${understood ? palette.accent : palette.g300}`,
                    background: understood ? palette.accent : 'transparent',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: 2,
                  }}
                >
                  {understood && <Check size={14} color={palette.white} strokeWidth={3} />}
                </span>
                <span style={{ fontSize: typography.size.body, color: palette.g700, lineHeight: 1.4 }}>
                  I understand ITC claims are excluded under Quick Method.
                </span>
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <div style={{ fontSize: typography.size.title, fontWeight: 700, color: palette.g800 }}>
                Confirm switch
              </div>
              <div style={{ fontSize: typography.size.body, color: palette.g600, lineHeight: 1.5 }}>
                Switching tax mode takes effect immediately. Past filings stay locked. Future remittances
                use the new rate.
              </div>
              <MetricRow label="Effective" value="Today" />
              {errorMsg && (
                <div style={{ fontSize: typography.size.caption, color: palette.amber, fontWeight: 600 }}>
                  {errorMsg}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: spacing.sm, padding: spacing.lg,
          borderTop: `1px solid ${palette.g150}`,
        }}>
          {step > 1 ? (
            <button
              type="button"
              onClick={goBack}
              disabled={busy}
              style={{
                flex: 1,
                padding: '10px 22px',
                borderRadius: radii.md,
                border: `1px solid ${palette.g200}`,
                background: palette.white,
                color: palette.g700,
                fontSize: typography.size.subheading,
                fontWeight: 600,
                cursor: busy ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Back
            </button>
          ) : (
            <div style={{ flex: 1 }} />
          )}
          {step < 3 ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance}
              style={{
                flex: 1,
                padding: '10px 22px',
                borderRadius: radii.md,
                border: 'none',
                background: canAdvance ? palette.accent : palette.g300,
                color: palette.white,
                fontSize: typography.size.subheading,
                fontWeight: 700,
                cursor: canAdvance ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
              }}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              style={{
                flex: 1,
                padding: '10px 22px',
                borderRadius: radii.md,
                border: 'none',
                background: busy ? palette.g300 : palette.accent,
                color: palette.white,
                fontSize: typography.size.subheading,
                fontWeight: 700,
                cursor: busy ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {busy ? 'Saving…' : 'Confirm'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      paddingBlock: spacing.sm, borderBottom: `1px solid ${palette.g150}`,
    }}>
      <span style={{ fontSize: typography.size.body, color: palette.g700 }}>{label}</span>
      <span style={{
        fontSize: typography.size.body, color: palette.g800, fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
      }}>{value}</span>
    </div>
  );
}
